import React, { useEffect, useState, useMemo } from "react";
import {
  Plus, MapPin, User, Phone, Car, Clock, CheckCircle2, XCircle,
  DollarSign, Route, Play, X, Save, RefreshCw, ChevronRight, Wallet,
  Map as MapIcon, Zap,
} from "lucide-react";
import { dataApi, api, formatApiError } from "../api/client";
import MapView, { ICONS } from "../components/MapView";

const STATUS_META = {
  pending:     { label: "Pendiente",  color: "warning", icon: Clock },
  assigned:    { label: "Asignada",   color: "info",    icon: User },
  in_progress: { label: "En curso",   color: "info",    icon: Route },
  completed:   { label: "Finalizada", color: "success", icon: CheckCircle2 },
  cancelled:   { label: "Cancelada",  color: "danger",  icon: XCircle },
};

const PAY_METHODS = [
  { v: "cash", l: "Efectivo" },
  { v: "yape", l: "Yape" },
  { v: "plin", l: "Plin" },
  { v: "card", l: "Tarjeta" },
  { v: "transfer", l: "Transferencia" },
];

export default function RidesView() {
  const [rides, setRides] = useState([]);
  const [zones, setZones] = useState([]);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("active");
  const [showNew, setShowNew] = useState(false);
  const [assigning, setAssigning] = useState(null);   // ride
  const [completing, setCompleting] = useState(null); // ride
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const [r, z] = await Promise.all([dataApi.rides(), dataApi.zones()]);
      setRides(r); setZones(z);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setRefreshing(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const grouped = useMemo(() => ({
    pending:     rides.filter((r) => r.status === "pending"),
    assigned:    rides.filter((r) => r.status === "assigned"),
    in_progress: rides.filter((r) => r.status === "in_progress"),
    completed:   rides.filter((r) => r.status === "completed"),
    cancelled:   rides.filter((r) => r.status === "cancelled"),
  }), [rides]);

  const visible = tab === "active"
    ? [...grouped.pending, ...grouped.assigned, ...grouped.in_progress]
    : tab === "completed" ? grouped.completed
    : tab === "cancelled" ? grouped.cancelled
    : rides;

  const action = async (fn, msg = "") => {
    try { await fn(); load(); }
    catch (e) { alert(formatApiError(e)); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="rides-view">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-[#3949AB]">Operaciones</div>
          <h1 className="font-display font-bold text-2xl">Carreras</h1>
          <p className="text-sm text-neutral-500 mt-1">Panel de despacho — asigna, sigue y cierra carreras en tiempo real.</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost" onClick={load} disabled={refreshing} data-testid="rides-refresh">
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} strokeWidth={2.5}/>Actualizar
          </button>
          <button className="btn-primary" onClick={() => setShowNew(true)} data-testid="rides-btn-new">
            <Plus className="w-4 h-4" strokeWidth={2.5}/> Nueva carrera
          </button>
        </div>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Pendientes" value={grouped.pending.length} color="warning"/>
        <Kpi label="Asignadas" value={grouped.assigned.length} color="info"/>
        <Kpi label="En curso" value={grouped.in_progress.length} color="info"/>
        <Kpi label="Finalizadas" value={grouped.completed.length} color="success"/>
        <Kpi label="Canceladas" value={grouped.cancelled.length} color="danger"/>
      </div>

      {/* Tabs */}
      <div className="border-b border-neutral-200">
        <div className="flex gap-1 flex-wrap">
          {[
            { k: "active", l: "Activas", count: grouped.pending.length + grouped.assigned.length + grouped.in_progress.length },
            { k: "map", l: "Mapa", count: null, icon: MapIcon },
            { k: "completed", l: "Finalizadas", count: grouped.completed.length },
            { k: "cancelled", l: "Canceladas", count: grouped.cancelled.length },
            { k: "all", l: "Todas", count: rides.length },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`rides-tab-${t.k}`}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 flex items-center gap-2 ${
                tab === t.k ? "border-[#3949AB] text-[#3949AB]" : "border-transparent text-neutral-500 hover:text-neutral-800"
              }`}>
              {t.icon && <t.icon className="w-4 h-4" strokeWidth={2.5}/>}
              {t.l}{t.count !== null && <span className="chip chip-info !py-0 !px-1.5 !text-[10px]">{t.count}</span>}
            </button>
          ))}
        </div>
      </div>

      {err && <div className="chip chip-danger">{err}</div>}

      {tab === "map" ? (
        <MapView
          height={520}
          testid="rides-map"
          markers={[
            ...zones.filter((z) => z.lat && z.lng).map((z) => ({
              lat: z.lat, lng: z.lng, icon: ICONS.zone,
              label: z.name, popup: `${z.code} · Base S/ ${z.base_price?.toFixed(2)}`,
            })),
            ...[...grouped.pending, ...grouped.assigned, ...grouped.in_progress]
              .filter((r) => r.origin_lat && r.origin_lng)
              .map((r) => ({
                lat: r.origin_lat, lng: r.origin_lng, icon: ICONS.origin,
                label: `Carrera #${r.id.slice(0, 8)}`,
                popup: `${r.passenger_name} · ${r.origin_zone_name} → ${r.destination_zone_name} · S/ ${(r.final_fare || r.estimated_fare)?.toFixed(2)}`,
              })),
          ]}
          routes={[...grouped.pending, ...grouped.assigned, ...grouped.in_progress]
            .filter((r) => r.origin_lat && r.destination_lat)
            .map((r) => [[r.origin_lat, r.origin_lng], [r.destination_lat, r.destination_lng]])}
        />
      ) : visible.length === 0 ? (
        <div className="office-card p-12 text-center">
          <div className="text-4xl mb-2">🚕</div>
          <div className="text-neutral-500">Sin carreras en esta vista.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {visible.map((r) => (
            <RideCard key={r.id} ride={r}
              onAssign={() => setAssigning(r)}
              onAutoAssign={async () => {
                try { await api.post(`/rides/${r.id}/auto-assign`); load(); }
                catch (e) { alert(formatApiError(e)); }
              }}
              onStart={() => action(() => dataApi.startRide(r.id))}
              onComplete={() => setCompleting(r)}
              onCancel={() => { if (window.confirm(`¿Cancelar carrera de ${r.passenger_name}?`)) action(() => dataApi.cancelRide(r.id)); }}/>
          ))}
        </div>
      )}

      {showNew && <NewRideModal zones={zones}
        onClose={() => setShowNew(false)}
        onSaved={() => { setShowNew(false); load(); }}/>}
      {assigning && <AssignModal ride={assigning}
        onClose={() => setAssigning(null)}
        onSaved={() => { setAssigning(null); load(); }}/>}
      {completing && <CompleteModal ride={completing}
        onClose={() => setCompleting(null)}
        onSaved={() => { setCompleting(null); load(); }}/>}
    </div>
  );
}

const Kpi = ({ label, value, color }) => {
  const borderClass = {
    warning: "border-l-[#F5A623]",
    success: "border-l-[#107C10]",
    danger:  "border-l-[#D13438]",
    info:    "border-l-[#3949AB]",
  }[color] || "border-l-[#3949AB]";
  return (
    <div className={`office-card p-3 border-l-4 ${borderClass}`}>
      <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="text-2xl font-display font-bold">{value}</div>
    </div>
  );
};

const RideCard = ({ ride, onAssign, onAutoAssign, onStart, onComplete, onCancel }) => {
  const meta = STATUS_META[ride.status];
  const Icon = meta.icon;
  return (
    <div className="office-card p-4" data-testid={`ride-card-${ride.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`chip chip-${meta.color}`}><Icon className="w-3 h-3" strokeWidth={2.5}/>{meta.label}</span>
            <span className="text-xs text-neutral-400">#{ride.id.slice(0, 8)}</span>
            <span className="text-xs text-neutral-400 ml-auto">{new Date(ride.requested_at).toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit" })}</span>
          </div>
          <div className="font-semibold flex items-center gap-1.5 text-neutral-800">
            <User className="w-3.5 h-3.5 text-neutral-400"/>{ride.passenger_name}
          </div>
          <div className="text-xs text-neutral-500 flex items-center gap-1.5 mt-0.5">
            <Phone className="w-3 h-3"/>{ride.passenger_phone}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-neutral-500">Tarifa</div>
          <div className="font-mono font-bold text-[#3949AB] text-lg">S/ {(ride.final_fare || ride.estimated_fare)?.toFixed(2)}</div>
          {ride.verification_code && ride.status === "assigned" && !ride.verification_code_used && (
            <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-300 rounded text-[10px] font-mono font-bold text-amber-700" title="Código de seguridad que dará el pasajero al conductor para iniciar el viaje">
              🔐 {ride.verification_code}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 text-sm py-2 border-y border-neutral-100">
        <MapPin className="w-4 h-4 text-emerald-600 flex-shrink-0"/>
        <div className="flex-1 min-w-0 truncate">
          <span className="font-medium">{ride.origin_zone_name}</span>
          {ride.origin_address && <span className="text-xs text-neutral-500"> · {ride.origin_address}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 text-sm py-2">
        <ChevronRight className="w-4 h-4 text-neutral-300 flex-shrink-0"/>
        <MapPin className="w-4 h-4 text-red-500 flex-shrink-0"/>
        <div className="flex-1 min-w-0 truncate">
          <span className="font-medium">{ride.destination_zone_name}</span>
          {ride.destination_address && <span className="text-xs text-neutral-500"> · {ride.destination_address}</span>}
        </div>
      </div>

      {ride.driver_name && (
        <div className="mt-2 pt-2 border-t border-neutral-100 flex items-center gap-2 text-sm">
          <Car className="w-4 h-4 text-[#3949AB]"/>
          <span className="font-medium">{ride.driver_name}</span>
          {ride.vehicle_plate && <span className="chip chip-info !text-[10px] !py-0.5">{ride.vehicle_plate}</span>}
        </div>
      )}

      {/* Acciones según estado */}
      <div className="mt-3 flex gap-2 flex-wrap">
        {ride.status === "pending" && (
          <>
            <button className="btn-primary !py-1.5 !text-xs" onClick={onAutoAssign} data-testid={`ride-autoassign-${ride.id}`}>
              <Zap className="w-3 h-3" strokeWidth={2.5}/>Auto-asignar
            </button>
            <button className="btn-secondary !py-1.5 !text-xs" onClick={onAssign} data-testid={`ride-assign-${ride.id}`}>
              <User className="w-3 h-3" strokeWidth={2.5}/>Elegir conductor
            </button>
            <button className="btn-ghost !py-1.5 !text-xs !text-[#D13438]" onClick={onCancel}>
              <XCircle className="w-3 h-3" strokeWidth={2.5}/>Cancelar
            </button>
          </>
        )}
        {ride.status === "assigned" && (
          <>
            <button className="btn-primary !py-1.5 !text-xs" onClick={onStart} data-testid={`ride-start-${ride.id}`}>
              <Play className="w-3 h-3" strokeWidth={2.5}/>Iniciar
            </button>
            <button className="btn-ghost !py-1.5 !text-xs !text-[#D13438]" onClick={onCancel}>Cancelar</button>
          </>
        )}
        {ride.status === "in_progress" && (
          <button className="btn-primary !py-1.5 !text-xs" onClick={onComplete} data-testid={`ride-complete-${ride.id}`}>
            <CheckCircle2 className="w-3 h-3" strokeWidth={2.5}/>Finalizar
          </button>
        )}
        {(ride.status === "completed" || ride.status === "cancelled") && (
          <span className="text-xs text-neutral-400">
            {ride.status === "completed" && ride.completed_at && `Cerrada ${new Date(ride.completed_at).toLocaleString("es-PE")}`}
            {ride.status === "cancelled" && ride.cancelled_at && `Cancelada ${new Date(ride.cancelled_at).toLocaleString("es-PE")}`}
          </span>
        )}
      </div>
    </div>
  );
};

const NewRideModal = ({ zones, onClose, onSaved }) => {
  const [form, setForm] = useState({
    passenger_name: "", passenger_phone: "",
    origin_zone_id: zones[0]?.id || "", origin_address: "",
    destination_zone_id: zones[1]?.id || zones[0]?.id || "", destination_address: "",
    vehicle_type: "sedan", payment_method: "cash", channel: "dispatcher", notes: "",
  });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    try { await dataApi.createRide(form); onSaved(); }
    catch (e) { setErr(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose}></div>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-6" onClick={onClose}>
        <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-lg shadow-xl max-w-lg w-full" data-testid="new-ride-modal">
          <div className="border-b px-5 py-3 flex items-center justify-between">
            <h3 className="font-display font-semibold text-lg">Nueva carrera</h3>
            <button type="button" onClick={onClose} className="btn-ghost !p-1.5"><X className="w-4 h-4" strokeWidth={2.5}/></button>
          </div>
          <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Pasajero *</label>
                <input value={form.passenger_name} onChange={(e) => setForm({...form, passenger_name: e.target.value})}
                  className="office-input mt-1.5" placeholder="Nombre completo" required data-testid="new-ride-name"/>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Teléfono *</label>
                <input value={form.passenger_phone} onChange={(e) => setForm({...form, passenger_phone: e.target.value})}
                  className="office-input mt-1.5" placeholder="+51 ..." required data-testid="new-ride-phone"/>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Zona origen *</label>
              <select value={form.origin_zone_id} onChange={(e) => setForm({...form, origin_zone_id: e.target.value})} className="office-input mt-1.5" data-testid="new-ride-origin">
                {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
              </select>
              <input value={form.origin_address} onChange={(e) => setForm({...form, origin_address: e.target.value})}
                className="office-input mt-1.5" placeholder="Dirección específica (opcional)" data-testid="new-ride-origin-addr"/>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Zona destino *</label>
              <select value={form.destination_zone_id} onChange={(e) => setForm({...form, destination_zone_id: e.target.value})} className="office-input mt-1.5" data-testid="new-ride-dest">
                {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
              </select>
              <input value={form.destination_address} onChange={(e) => setForm({...form, destination_address: e.target.value})}
                className="office-input mt-1.5" placeholder="Dirección específica (opcional)" data-testid="new-ride-dest-addr"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Tipo vehículo</label>
                <select value={form.vehicle_type} onChange={(e) => setForm({...form, vehicle_type: e.target.value})} className="office-input mt-1.5">
                  <option value="sedan">Sedán</option>
                  <option value="hatchback">Hatchback</option>
                  <option value="suv">SUV</option>
                  <option value="station_wagon">Station Wagon</option>
                  <option value="pickup">Pickup</option>
                  <option value="van">Van</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Pago</label>
                <select value={form.payment_method} onChange={(e) => setForm({...form, payment_method: e.target.value})} className="office-input mt-1.5">
                  {PAY_METHODS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Notas</label>
              <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}
                className="office-input mt-1.5" rows={2} placeholder="Referencias, indicaciones..." data-testid="new-ride-notes"/>
            </div>
            {err && <div className="chip chip-danger">{err}</div>}
          </div>
          <div className="border-t px-5 py-3 flex justify-end gap-2 bg-neutral-50">
            <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="new-ride-submit">
              <Save className="w-3.5 h-3.5" strokeWidth={2.5}/>{saving ? "Creando..." : "Crear carrera"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const AssignModal = ({ ride, onClose, onSaved }) => {
  const [drivers, setDrivers] = useState([]);
  const [selected, setSelected] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dataApi.drivers(true).then((d) => { setDrivers(d); if (d[0]) setSelected(d[0].id); })
      .catch((e) => setErr(formatApiError(e)));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try { await dataApi.assignRide(ride.id, selected); onSaved(); }
    catch (e) { setErr(formatApiError(e)); } finally { setLoading(false); }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose}></div>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-6" onClick={onClose}>
        <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-lg shadow-xl max-w-lg w-full" data-testid="assign-modal">
          <div className="border-b px-5 py-3 flex items-center justify-between">
            <div>
              <h3 className="font-display font-semibold text-lg">Asignar conductor</h3>
              <div className="text-xs text-neutral-500">Carrera de {ride.passenger_name} · {ride.origin_zone_name} → {ride.destination_zone_name}</div>
            </div>
            <button type="button" onClick={onClose} className="btn-ghost !p-1.5"><X className="w-4 h-4" strokeWidth={2.5}/></button>
          </div>
          <div className="p-5 space-y-3">
            {drivers.length === 0 ? (
              <div className="text-sm text-neutral-500 py-4 text-center">
                No hay conductores disponibles. Verifica que existan usuarios con rol Conductor o Socio y que no tengan carrera en curso.
              </div>
            ) : (
              <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="drivers-list">
                {drivers.map((d) => (
                  <label key={d.id} className={`flex items-center gap-3 p-3 rounded border cursor-pointer ${
                    selected === d.id ? "border-[#3949AB] bg-[#EEF0FC]" : "border-neutral-200 hover:bg-neutral-50"
                  }`}>
                    <input type="radio" name="driver" value={d.id} checked={selected === d.id}
                      onChange={() => setSelected(d.id)} data-testid={`driver-${d.id}`}/>
                    <div className="w-8 h-8 rounded-full bg-white text-[#3949AB] flex items-center justify-center font-semibold text-sm border">
                      {d.name.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{d.name}</div>
                      <div className="text-xs text-neutral-500 flex items-center gap-2">
                        <Phone className="w-3 h-3"/>{d.phone}
                        {d.vehicle_plate && <><span>·</span><Car className="w-3 h-3"/>{d.vehicle_plate}</>}
                      </div>
                    </div>
                    <span className="chip chip-info !text-[10px]">{d.role === "socio" ? "Socio" : "Conductor"}</span>
                  </label>
                ))}
              </div>
            )}
            {err && <div className="chip chip-danger">{err}</div>}
          </div>
          <div className="border-t px-5 py-3 flex justify-end gap-2 bg-neutral-50">
            <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={loading || !selected || drivers.length === 0} className="btn-primary" data-testid="assign-submit">
              {loading ? "Asignando..." : "Confirmar asignación"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const CompleteModal = ({ ride, onClose, onSaved }) => {
  const [form, setForm] = useState({
    final_fare: ride.estimated_fare,
    payment_method: ride.payment_method || "cash",
    notes: ride.notes || "",
  });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    try { await dataApi.completeRide(ride.id, form); onSaved(); }
    catch (e) { setErr(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose}></div>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-6" onClick={onClose}>
        <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-lg shadow-xl max-w-md w-full" data-testid="complete-modal">
          <div className="border-b px-5 py-3 flex items-center justify-between">
            <h3 className="font-display font-semibold text-lg">Finalizar carrera</h3>
            <button type="button" onClick={onClose} className="btn-ghost !p-1.5"><X className="w-4 h-4" strokeWidth={2.5}/></button>
          </div>
          <div className="p-5 space-y-3">
            <div className="chip chip-info !flex-col !items-start w-full">
              <div className="text-xs">Estimado inicial:</div>
              <div className="font-mono font-bold">S/ {ride.estimated_fare?.toFixed(2)}</div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Tarifa final (S/) *</label>
              <div className="relative mt-1.5">
                <DollarSign className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2"/>
                <input type="number" step="0.5" min="0" value={form.final_fare}
                  onChange={(e) => setForm({...form, final_fare: parseFloat(e.target.value) || 0})}
                  className="office-input pl-9 font-mono" required data-testid="complete-fare"/>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Método de pago</label>
              <select value={form.payment_method} onChange={(e) => setForm({...form, payment_method: e.target.value})}
                className="office-input mt-1.5" data-testid="complete-payment">
                {PAY_METHODS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Notas de cierre</label>
              <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}
                className="office-input mt-1.5" rows={2}/>
            </div>
            {err && <div className="chip chip-danger">{err}</div>}
          </div>
          <div className="border-t px-5 py-3 flex justify-end gap-2 bg-neutral-50">
            <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="complete-submit">
              <Wallet className="w-3.5 h-3.5" strokeWidth={2.5}/>{saving ? "Cerrando..." : "Finalizar y cobrar"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};
